import { useState, useCallback } from "react";
import {
  SectionCategoryService,
  CreateSectionCategoryRequest,
  UpdateSectionCategoryRequest,
} from "@/services/sectionCategoryService";
import { SectionCategory } from "@/types/models/page";
import { PaginatedApiResponse } from "@/lib/apiClient";

interface UseSectionCategoriesState {
  categories: SectionCategory[];
  loading: boolean;
  error: string | null;
  totalCategories: number;
  currentPage: number;
  totalPages: number;
}

interface UseSectionCategoriesActions {
  fetchCategories: (
    page?: number,
    pageSize?: number,
    search?: string,
  ) => Promise<void>;
  fetchCategoryById: (categoryId: string) => Promise<SectionCategory>;
  createCategory: (data: CreateSectionCategoryRequest) => Promise<void>;
  updateCategory: (
    categoryId: string,
    data: UpdateSectionCategoryRequest,
  ) => Promise<void>;
  deleteCategory: (categoryId: string) => Promise<void>;
  resetError: () => void;
}

export const useSectionCategories = (): UseSectionCategoriesState &
  UseSectionCategoriesActions => {
  const [state, setState] = useState<UseSectionCategoriesState>({
    categories: [],
    loading: false,
    error: null,
    totalCategories: 0,
    currentPage: 1,
    totalPages: 1,
  });

  const fetchCategories = useCallback(
    async (page = 1, pageSize = 20, search?: string) => {
      console.log("🔍 Fetching section categories:", { page, pageSize, search });
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const response: PaginatedApiResponse<SectionCategory[]> =
          await SectionCategoryService.getSectionCategories(
            page,
            pageSize,
            search,
          );

        console.log("📦 Section categories response:", response);
        console.log("📋 Categories data:", response.data);
        console.log("📊 Total categories:", response.pagination?.total);
        console.log("🔢 Categories array length:", response.data?.length);

        // Validate response structure
        if (!response || !response.data) {
          console.error("❌ Invalid response structure:", response);
          setState((prev) => ({
            ...prev,
            error: "Invalid response from server",
            loading: false,
          }));
          return;
        }

        setState((prev) => ({
          ...prev,
          categories: response.data,
          totalCategories: response.pagination.total,
          currentPage: response.pagination.page,
          totalPages: response.pagination.totalPages,
          loading: false,
        }));

        console.log("✅ State updated with categories count:", response.data.length);
      } catch (error) {
        console.error("❌ Error fetching section categories:", error);
        console.error("❌ Error details:", {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
        });
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Failed to fetch section categories",
          loading: false,
        }));
      }
    },
    [],
  );

  const fetchCategoryById = useCallback(
    async (categoryId: string): Promise<SectionCategory> => {
      try {
        const category =
          await SectionCategoryService.getSectionCategoryById(categoryId);
        return category;
      } catch (error) {
        console.error("Error fetching section category:", error);
        throw error;
      }
    },
    [],
  );

  const createCategory = useCallback(
    async (data: CreateSectionCategoryRequest) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const newCategory =
          await SectionCategoryService.createSectionCategory(data);
        setState((prev) => ({
          ...prev,
          categories: [newCategory, ...prev.categories],
          totalCategories: prev.totalCategories + 1,
          loading: false,
        }));
      } catch (error) {
        console.error("Error creating section category:", error);
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Failed to create section category",
          loading: false,
        }));
        throw error;
      }
    },
    [],
  );

  const updateCategory = useCallback(
    async (categoryId: string, data: UpdateSectionCategoryRequest) => {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      try {
        const updatedCategory =
          await SectionCategoryService.updateSectionCategory(categoryId, data);
        setState((prev) => ({
          ...prev,
          categories: prev.categories.map((cat) =>
            cat.id === categoryId ? updatedCategory : cat,
          ),
          loading: false,
        }));
      } catch (error) {
        console.error("Error updating section category:", error);
        setState((prev) => ({
          ...prev,
          error:
            error instanceof Error
              ? error.message
              : "Failed to update section category",
          loading: false,
        }));
        throw error;
      }
    },
    [],
  );

  const deleteCategory = useCallback(async (categoryId: string) => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await SectionCategoryService.deleteSectionCategory(categoryId);
      setState((prev) => ({
        ...prev,
        categories: prev.categories.filter((cat) => cat.id !== categoryId),
        totalCategories: prev.totalCategories - 1,
        loading: false,
      }));
    } catch (error) {
      console.error("Error deleting section category:", error);
      setState((prev) => ({
        ...prev,
        error:
          error instanceof Error
            ? error.message
            : "Failed to delete section category",
        loading: false,
      }));
      throw error;
    }
  }, []);

  const resetError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    fetchCategories,
    fetchCategoryById,
    createCategory,
    updateCategory,
    deleteCategory,
    resetError,
  };
};

// If section categories need to be global, move to Redux. Otherwise, keep as is for now.
